import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EntityMultiSelect,
  type EntityMultiSelectOption,
} from '@/components/entity-multi-select/entity-multi-select';
import { useGetOfficesAgendasQuery } from '@/hooks/queries/use-get-offices-agendas';

type OfficeOption = {
  id: string;
  name: string;
};

type CommonProps = {
  /** Provide offices explicitly; otherwise they are fetched. */
  offices?: OfficeOption[];
  className?: string;
};

type ToggleProps = CommonProps & {
  mode?: 'toggle';
  value: string[];
  onValueChange: (officeIds: string[]) => void;
};

type SplitProps = CommonProps & {
  mode: 'split';
  value: string[] | undefined;
  onValueChange: (officeIds: string[] | undefined) => void;
};

type SelectOfficeMultiProps = ToggleProps | SplitProps;

/**
 * Office multi-select filter. Thin wrapper around {@link EntityMultiSelect}
 * that fetches all offices when none are provided.
 *
 * @param props - selection mode, current value and change callback
 * @returns the office multi-select
 */
export const SelectOfficeMulti = (props: SelectOfficeMultiProps) => {
  const { t } = useTranslation();
  const { data: officesResponse, isLoading: isRemoteLoading } =
    useGetOfficesAgendasQuery({}, { enabled: props.offices === undefined });

  const options = useMemo<EntityMultiSelectOption[]>(() => {
    const offices = props.offices ?? officesResponse?.data ?? [];
    return offices.map((office) => ({ id: office.id, label: office.name }));
  }, [props.offices, officesResponse]);

  const loading = props.offices === undefined && isRemoteLoading;
  const className = props.className ?? 'w-full';

  if (props.mode === 'split') {
    return (
      <EntityMultiSelect
        mode="split"
        options={options}
        label={t('select-office.label')}
        searchable
        loading={loading}
        className={className}
        value={props.value}
        onValueChange={props.onValueChange}
      />
    );
  }

  return (
    <EntityMultiSelect
      options={options}
      label={t('select-office.label')}
      searchable
      loading={loading}
      className={className}
      value={props.value}
      onValueChange={props.onValueChange}
    />
  );
};
